import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../lib/api'

export interface Outlet {
	_id?: string
	id?: string
	code: string
	name: string
	nameAr?: string
	address?: string
	phone?: string
	email?: string
	isStore: boolean
	isMainStore: boolean
	isActive: boolean
	createdAt?: string
	updatedAt?: string
}

export interface CreateOutletData {
	code: string
	name: string
	nameAr?: string
	address?: string
	phone?: string
	email?: string
	isStore?: boolean
	isMainStore?: boolean
	isActive?: boolean
}

export interface UpdateOutletData {
	name?: string
	nameAr?: string
	address?: string
	phone?: string
	email?: string
	isStore?: boolean
	isMainStore?: boolean
	isActive?: boolean
}

interface OutletsResponse {
	data: Outlet[]
	pagination: {
		page: number
		limit: number
		total: number
		totalPages: number
	}
}

interface OutletsParams {
	page?: number
	limit?: number
	search?: string
	isActive?: boolean
	isStore?: boolean
}

export const useOutlets = (params: OutletsParams = {}) => {
	const { page = 1, limit = 20, search, isActive, isStore } = params

	return useQuery({
		queryKey: ['outlets', { page, limit, search, isActive, isStore }],
		queryFn: () =>
			apiClient.get<OutletsResponse>('/outlets', {
				page,
				limit,
				search,
				isActive,
				isStore,
			}),
	})
}

export const useOutlet = (id: string) => {
	return useQuery({
		queryKey: ['outlet', id],
		queryFn: () => apiClient.get<Outlet>(`/outlets/${id}`),
		enabled: !!id,
	})
}

export const useOutletByCode = (code: string) => {
	return useQuery({
		queryKey: ['outlet', 'code', code],
		queryFn: () => apiClient.get<Outlet>(`/outlets/code/${code}`),
		enabled: !!code,
	})
}

export const useActiveOutlets = () => {
	return useQuery({
		queryKey: ['outlets', 'active'],
		queryFn: () => apiClient.get<{ data: Outlet[] }>('/outlets/active'),
	})
}

export const useStores = () => {
	return useQuery({
		queryKey: ['outlets', 'stores'],
		queryFn: () => apiClient.get<{ data: Outlet[] }>('/outlets/stores'),
	})
}

export const useCreateOutlet = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: (data: CreateOutletData) =>
			apiClient.post<Outlet>('/outlets', data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['outlets'] })
		},
	})
}

export const useUpdateOutlet = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: UpdateOutletData }) =>
			apiClient.patch<Outlet>(`/outlets/${id}`, data),
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: ['outlets'] })
			queryClient.invalidateQueries({ queryKey: ['outlet', variables.id] })
		},
	})
}

export const useDeleteOutlet = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: (id: string) => apiClient.delete(`/outlets/${id}`),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['outlets'] })
		},
	})
}
